import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { history } from '../../../store';
import { makeStyles } from '@material-ui/core/styles';
import ee from '../../../services/earth-engine';
import { runWaterlines } from '../../../algorithms/intertidal/waterlineRunner';
import { Box, Grid, Grow } from '@material-ui/core';
import GoogleMap from '../map/GoogleMap';
import ImageChooserCard from '../map/ImageChooserCard';
import ActionsCard from './ActionsCard';
import { ShapeList } from '..';
import LoadedImagesAccordion from '../map/LoadedImagesAccordion';
import { Actions as Map } from '../../../store/ducks/map';
import { useTranslation } from 'react-i18next';
import TourGuider from '../tour/TourGuider';
import { useLocalStorage } from '../../../common/utils';
import { Button } from '@material-ui/core';
import { OverlayHelper } from '../intertidal/IntertidalZoneDefiner';
//export shapefile dependencies
import Export from '../../../services/export';
import { INTERNALS } from '../../../common/metadata';
import { omit } from 'lodash';
import { CircularProgress, Typography } from '@material-ui/core';
import { aoiGeometry } from '../../../common/aoi';

/**
 * @function convertGeoJSONToLineStrings
 * @description Normalizes a GeoJSON FeatureCollection's features into LineStrings: converts
 * LinearRing/Polygon/MultiPolygon rings (dropping their closing point) and MultiLineString parts
 * into individual LineString features; LineString features pass through unchanged; any other
 * geometry type is dropped.
 * @param {Object} evaluated - GeoJSON FeatureCollection to normalize.
 * **Called by:
 * @see {WaterlineIdentificationAndLevelAssigment} - via startWaterlineRun and printWaterlines.
 * @see {fetchAndPlotSingleWaterline}
 * @memberof module:Intertidal
 * @returns {Object} The same FeatureCollection, with its `features` replaced by LineString features.
 */
function convertGeoJSONToLineStrings(evaluated) {
  if (!evaluated || evaluated.type !== 'FeatureCollection' || !Array.isArray(evaluated.features)) {
    throw new Error('O objeto fornecido deve ser um FeatureCollection GeoJSON.');
  }
  const newFeatures = [];
  evaluated.features.forEach((feature) => {
    if (!feature || feature.type !== 'Feature') return;
    const geom = feature.geometry;
    if (!geom) return;
    const props = feature.properties || {};
    const type = geom.type;
    const coords = geom.coordinates;
    const removeClosingPoint = (ring) => {
      if (ring.length >= 2) {
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (
          Array.isArray(first) &&
          Array.isArray(last) &&
          first.length === last.length &&
          first.every((val, i) => val === last[i])
        ) {
          return ring.slice(0, -1);
        }
      }
      return ring;
    };
    if (type === 'LinearRing') {
      if (Array.isArray(coords)) {
        const line = removeClosingPoint(coords);
        if (line.length >= 2) {
          newFeatures.push({
            type: 'Feature',
            properties: props,
            geometry: { type: 'LineString', coordinates: line },
          });
        }
      }
    } else if (type === 'Polygon') {
      if (Array.isArray(coords) && coords.length > 0 && Array.isArray(coords[0])) {
        const outerRing = removeClosingPoint(coords[0]);
        if (outerRing.length >= 2) {
          newFeatures.push({
            type: 'Feature',
            properties: props,
            geometry: { type: 'LineString', coordinates: outerRing },
          });
        }
      }
    } else if (type === 'MultiPolygon') {
      if (Array.isArray(coords)) {
        coords.forEach((polygon) => {
          if (Array.isArray(polygon) && polygon.length > 0 && Array.isArray(polygon[0])) {
            const outerRing = removeClosingPoint(polygon[0]);
            if (outerRing.length >= 2) {
              newFeatures.push({
                type: 'Feature',
                properties: props,
                geometry: { type: 'LineString', coordinates: outerRing },
              });
            }
          }
        });
      }
    } else if (type === 'MultiLineString') {
      if (Array.isArray(coords)) {
        coords.forEach((line) => {
          if (Array.isArray(line) && line.length >= 2) {
            newFeatures.push({
              type: 'Feature',
              properties: props,
              geometry: { type: 'LineString', coordinates: line },
            });
          }
        });
      }
    } else if (type === 'LineString') {
      if (Array.isArray(coords) && coords.length >= 2) {
        newFeatures.push({
          type: 'Feature',
          properties: props,
          geometry: { type: 'LineString', coordinates: coords },
        });
      }
    }
  });
  evaluated.features = newFeatures;
  return evaluated;
}

/**
 * @function prepareGeoJsonForGoogleMaps
 * @description Rewraps a sanitized FeatureCollection into a plain GeoJSON FeatureCollection of
 * LineString features suitable for plotting on Google Maps.
 * @param {Object} sanitizedEvaluated - FeatureCollection already normalized to LineStrings.
 * **Called by:
 * @see {WaterlineIdentificationAndLevelAssigment} - via printWaterlines and fetchAndPlotSingleWaterline.
 * @memberof module:Intertidal
 * @returns {Object|null} GeoJSON FeatureCollection ready for the map, or null if the input is empty/invalid.
 */
const prepareGeoJsonForGoogleMaps = (sanitizedEvaluated) => {
  if (!sanitizedEvaluated || !sanitizedEvaluated.features || sanitizedEvaluated.features.length === 0) {
    console.warn('SanitizedEvaluated inválido ou vazio.');
    return null;
  }
  return {
    type: 'FeatureCollection',
    features: sanitizedEvaluated.features.map((feature) => ({
      type: 'Feature',
      properties: feature.properties || {},
      geometry: {
        type: 'LineString',
        coordinates: feature.geometry.coordinates,
      },
    })),
  };
};

let evaluated = null;
let evaluatedEE = null;

export class WaterLinesExport {
  /**
   * @function exportShapeFile
   * @description Exports the currently plotted waterlines (`evaluatedEE`) as a shapefile group,
   * stripping the internal `INTERNALS` metadata property from each feature first.
   * @see {@link Export.table.toDevice.asShapefileGroup}
   * **Called by:
   * @see {ActionList} - via the "Export data" list item's onClick.
   * @memberof module:Intertidal
   * @returns {void}
   */
  static exportShapeFile = function () {
    const featuresSanitized = evaluatedEE.features.map((f) => ({
      ...f,
      properties: omit(f.properties || {}, [INTERNALS]),
    }));

    Export.table.toDevice.asShapefileGroup(
      [{ features: featuresSanitized }],
      Export.defaultOptions.device.shapefileGroup('output', 'vectors'),
    );
  };
}

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * waterline identification and level assignment page.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Intertidal
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
  },
  mapContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    flexFlow: 'column',
  },
  mapOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 10,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
  },
  margin: {
    margin: '5px',
  },
  right: {
    textAlign: 'right',
  },
}));

/**
 * @function thresholdingAlgorithm
 * @description Computes Otsu's dynamic threshold from an NDWI histogram: for each possible split
 * point, computes the between-class variance of the two resulting groups, and returns the mean
 * value that maximizes it.
 * @param {ee.Dictionary} histogramData - Histogram dictionary with `histogram` (counts) and `bucketMeans` keys.
 * **Called by:
 * @see {calculateWaterlineForImage}
 * @see {identifyWaterFeature}
 * @memberof module:Intertidal
 * @returns {ee.Number|null} The Otsu threshold value, or null if no histogram data was given.
 */
var thresholdingAlgorithm = function (histogramData) {
  if (!histogramData) {
    console.error('thresholdingAlgorithm recebeu um histograma nulo.');
    return null;
  }
  var counts = ee.Array(ee.Dictionary(histogramData).get('histogram'));
  var means = ee.Array(ee.Dictionary(histogramData).get('bucketMeans'));
  var size = means.length().get([0]);
  var total = counts.reduce(ee.Reducer.sum(), [0]).get([0]);
  var sum = means.multiply(counts).reduce(ee.Reducer.sum(), [0]).get([0]);
  var mean = sum.divide(total);
  var indices = ee.List.sequence(1, size);

  var bss = indices.map(function (i) {
    var aCounts = counts.slice(0, 0, i);
    var aCount = aCounts.reduce(ee.Reducer.sum(), [0]).get([0]);
    var aMeans = means.slice(0, 0, i);
    var aMean = aMeans.multiply(aCounts).reduce(ee.Reducer.sum(), [0]).get([0]).divide(aCount);
    var bCount = total.subtract(aCount);
    var bMean = sum.subtract(aCount.multiply(aMean)).divide(bCount);
    return aCount.multiply(aMean.subtract(mean).pow(2)).add(bCount.multiply(bMean.subtract(mean).pow(2)));
  });

  return means.sort(bss).get([-1]);
};

/**
 * @function WaterlineIdentificationAndLevelAssigment
 * @description Automatically identifies the waterline for every acquired image and assigns each
 * one a tide level from the imported tide gauge data.
 * 1. Waits for the map to be ready, then centers it on the AOI.
 * 2. Builds the tide gauge FeatureCollection from the imported tide data (timezone-corrected).
 * 3. Sequentially processes each cached image: derives its NDWI waterline via Otsu thresholding,
 *    smooths it with a Gaussian filter, matches it to the closest tide gauge reading, and plots
 *    it on the map with a per-image color.
 * 4. Accumulates every processed waterline into `evaluatedEE` for later export.
 * @see {@link calculateWaterlineForImage}
 * @see {@link startWaterlineRun}
 * @see {@link plotEvaluatedSync}
 * **Called by:
 * @see {ProcessingIntertidalPage} - as the intertidal processing step.
 * @memberof module:Intertidal
 * @returns {JSX.Element} The map, processing progress indicator, and navigation controls.
 */
const WaterlineIdentificationAndLevelAssigment = () => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const mapContainerRef = useRef(null);

  const [processingStarted, setProcessingStarted] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const [timeEstimate, setTimeEstimate] = useState('');
  const timerRef = useRef(null);
  const colors = [
    '#FF0000',
    '#00FF00',
    '#0000FF',
    '#FFFF00',
    '#00FFFF',
    '#FF00FF',
    '#FFA500',
    '#800080',
    '#008080',
    '#000000',
  ];
  useEffect(() => {
    if (map) {
      const mapClickListener = window.google.maps.event.addListener(map, 'click', () => {
        if (selectedPolyline) {
          selectedPolyline.setOptions({
            strokeWeight: 2,
            strokeColor: selectedPolyline.originalColor,
          });
        }
        if (selectedInfoWindow) {
          selectedInfoWindow.close();
        }
        selectedPolyline = null;
        selectedInfoWindow = null;
      });
      return () => {
        window.google.maps.event.removeListener(mapClickListener);
      };
    }
  }, [map]);

  useEffect(() => {
    if (mapReady && map) {
      const coordinatesArray = JSON.parse(window.localStorage.getItem('AOIIntertidal'));
      if (coordinatesArray && coordinatesArray.length > 0) {
        dispatch(Map.centralizeMap(coordinatesArray));
      }

      console.log('Mapa pronto. Acionando inicio do processamento em 500ms.');
      setTimeout(() => {
        setProcessingStarted(true);
      }, 500);
    }
  }, [mapReady, map]);

  /**
   * @function calculateWaterlineForImage
   * @description Derives the smoothed, tide-tagged waterline for a single image.
   * 1. Skips processing (returns an empty collection) if the image has no valid NDWI pixels.
   * 2. Computes the NDWI histogram and its Otsu dynamic threshold, then vectorizes the
   *    water/land boundary at that threshold, keeping only polygons with at least
   *    `MIN_VERTICES` vertices.
   * 3. For each valid polygon, smooths its ring with a Gaussian filter and tags it with the
   *    image's datetime and the closest tide gauge reading.
   * @param {ee.Image} image - Image with an 'ndwi' band.
   * @param {ee.Geometry} geometry - Area of interest geometry.
   * @param {Number} scale - Vectorization scale, in meters.
   * @param {ee.FeatureCollection} tideGauge - Tide gauge readings to match against.
   * @see {@link thresholdingAlgorithm}
   * @see {@link linearGaussianFilter}
   * @see {@link getClosestFeatureImageAndDado}
   * **Called by:
   * @see {startWaterlineRun}
   * @memberof module:Intertidal
   * @returns {ee.FeatureCollection} Smoothed, tide-tagged waterline features (empty if none found).
   */
  const calculateWaterlineForImage = (image, geometry, scale, tideGauge) => {
    const ndwi = image.select('ndwi');
    const bandCount = ndwi
      .reduceRegion({ reducer: ee.Reducer.count(), geometry: geometry, scale: 10, maxPixels: 1e12 })
      .get('ndwi');

    const process = () => {
      const histogram = ndwi.reduceRegion({
        reducer: ee.Reducer.histogram(),
        geometry: geometry,
        scale: 10,
      });
      const dynamicThreshold = thresholdingAlgorithm(histogram.get('ndwi'));
      const waterLandVectors = ndwi
        .clip(geometry)
        .lt(dynamicThreshold)
        .reduceToVectors({ scale: scale, maxPixels: 1e12, eightConnected: false })
        .filter(ee.Filter.eq('label', 1));

      const MIN_VERTICES = 10;
      const cleanedFeatures = waterLandVectors
        .map((feature) => {
          const numVertices = ee.List(feature.geometry().coordinates().get(0)).size();
          return feature.set('numVertices', numVertices);
        })
        .filter(ee.Filter.gte('numVertices', MIN_VERTICES));

      return ee.Algorithms.If(
        cleanedFeatures.size().gt(0),
        processAllValidFeatures(cleanedFeatures, ndwi),
        ee.FeatureCollection([]),
      );
    };

    const processAllValidFeatures = (featureCollection, img) => {
      const tideResult = getClosestFeatureImageAndDado(img, tideGauge);
      const datetime = ee.Date(img.get('datetime')).format();
      const dtGaugeFormated = ee.Date(tideResult.dateGauge).format('yyyy-MM-dd HH:mm');
      const dtImageFormated = ee.Date(tideResult.dateImage).format('yyyy-MM-dd HH:mm');

      const allWaterlines = featureCollection
        .map((polygon) => {
          const rings = ee.List(polygon.geometry().coordinates());
          const processedRings = rings.map((ring) => {
            const line = ee.Geometry.LineString(ring);
            const smoothedCoords = linearGaussianFilter(line.coordinates());
            return ee.Feature(ee.Geometry.LineString(smoothedCoords));
          });
          return ee.FeatureCollection(processedRings);
        })
        .flatten();

      const finalWaterlines = allWaterlines.map((feature) => {
        return feature.set({
          dateTime: datetime,
          tideValue: tideResult.tideValue,
          dateGauge: dtGaugeFormated,
          dateImage: dtImageFormated,
        });
      });
      return finalWaterlines;
    };

    return ee.FeatureCollection(ee.Algorithms.If(ee.Number(bandCount).gt(0), process(), ee.FeatureCollection([])));
  };

  /**
   * @function startWaterlineRun
   * @description Asks Earth Engine for the waterline of every selected image, a few at a time, and
   * draws each one as its result lands.
   * @param {ee.ImageCollection} imageCollection - Full image collection being processed.
   * @param {Number} numImages - Total number of images in the collection.
   * @param {ee.Geometry} geometry - Area of interest geometry.
   * @param {Number} scale - Vectorization scale, in meters.
   * @see {@link runWaterlines} - why several run at once rather than one after another.
   * @see {@link calculateWaterlineForImage}
   * @see {@link convertGeoJSONToLineStrings}
   * @see {@link plotEvaluatedSync}
   * **Called by:
   * @see {WaterlineIdentificationAndLevelAssigment} - from the main processing effect.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const startWaterlineRun = (imageCollection, numImages, geometry, scale) => {
    const images = imageCollection.toList(numImages);
    setCurrentImage(0);
    runWaterlines({
      total: numImages,
      request: (index) => calculateWaterlineForImage(ee.Image(images.get(index)), geometry, scale, tideGauge),
      onResult: (index, waterlineGeoJSON, error) => {
        if (error) {
          console.error(`Erro ao processar imagem ${index + 1}:`, error);
          return;
        }
        if (!waterlineGeoJSON?.features?.length) {
          console.log(`Nenhuma linha d'água válida encontrada para a imagem ${index + 1}.`);
          return;
        }
        const sanitized = convertGeoJSONToLineStrings(waterlineGeoJSON);
        plotEvaluatedSync(map, sanitized, colors[index % colors.length]);
        if (!evaluatedEE) evaluatedEE = { type: 'FeatureCollection', features: [] };
        evaluatedEE.features.push(...sanitized.features);
      },
      onProgress: ({ completed, total }) => {
        setCurrentImage(completed);
        if (timerRef.current && completed > 0 && completed < total) {
          setTimeEstimate(timerRef.current.format(timerRef.current.project(completed, total)));
        }
      },
      onDone: () => {
        console.log('Processamento de todas as imagens concluído.');
        setIsProcessing(false);
        setTimeEstimate('');
      },
    });
  };

  useEffect(() => {
    if (mapReady && map) {
      console.log('Mapa pronto. Iniciando processo automático.');

      const coordinatesArray = JSON.parse(window.localStorage.getItem('AOIIntertidal'));
      if (coordinatesArray && coordinatesArray.length > 0) {
        dispatch(Map.centralizeMap(coordinatesArray));
      }

      const scale = 10;
      const geometry = aoiGeometry(coordinatesArray);
      const assetList = ee.List(OverlayHelper.imgsToStep5);
      const imageCollection = ee.ImageCollection(assetList);
      const imageCollectionWithDatetime = imageCollection.map(addDatetimeProperty);

      imageCollectionWithDatetime.size().evaluate((numImages, error) => {
        if (error) {
          console.error('Erro ao obter o tamanho da coleção:', error);
          return;
        }
        if (numImages > 0) {
          setTotalImages(numImages);
          setIsProcessing(true);
          startWaterlineRun(imageCollectionWithDatetime, numImages, geometry, scale);
        } else {
          console.log('Nenhuma imagem para processar.');
        }
      });
    }
  }, [mapReady, map]);

  const coordinates = useSelector((state) => state.acquisition.coordinates);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [t] = useTranslation();

  /**
   * @function displayROI
   * @description Displays the acquisition ROI as an outline feature and centers the map on it.
   * Not currently called anywhere in the codebase.
   * @memberof module:Intertidal
   * @returns {void}
   */
  const displayROI = () => {
    if (coordinates) {
      const feature = ee.Feature(aoiGeometry(coordinates));
      const border = feature.buffer(15).difference(feature);
      dispatch(Map.addEEFeature(border, 'forms.Map.roi', '#00A391', 0.6));
      dispatch(Map.centralizeMap(coordinates));
    }
  };

  /**
   * @function returnToBegin
   * @description Redirects the user back to the selection page to start a new analysis.
   * **Called by:
   * @see {WaterlineIdentificationAndLevelAssigment} - via the "Return to begin" button's onClick.
   * @memberof module:Intertidal
   * @returns {void}
   */
  let returnToBegin = function () {
    history.push('/main/selection');
  };

  const steps = [
    {
      selector: '#mapScreen',
      content: t('tour.map.start'),
    },
    {
      selector: '#imageChooserForm',
      content: t('tour.map.imageChooser.info'),
    },
    {
      selector: '#imageChooserSelect',
      content: t('tour.map.imageChooser.select'),
    },
    {
      selector: '#imageChooserLoadButton',
      content: t('tour.map.imageChooser.click'),
    },
  ];

  const [isTourOpen, setIsTourOpen] = useLocalStorage('showMapTour', true);

  let tideDataTable = JSON.parse(window.localStorage.getItem('tideData'));
  let features = [];

  tideDataTable.forEach((entry) => {
    const valorMare = parseFloat(entry[1]);
    const valorArredondado = valorMare.toFixed(2);
    let point = ee.Geometry.MultiPoint([], 'EPSG:4326');
    let f1 = ee.Feature(point, {
      dateTime: entry[0],
      Dado: ee.Number.parse(valorArredondado),
    });
    features.push(f1);
  });

  console.log("Primeiros 5 elementos no array 'tideDataTable':", tideDataTable.slice(0, 5));
  console.log("Primeiros 5 elementos no array 'tideDataTable':", features.slice(0, 5));

  var tideGauge = ee.FeatureCollection(features);

  var timezone = parseInt(window.localStorage.getItem('timezone') || '0', 10);
  console.log('Valor de timezone', timezone);

  tideGauge = tideGauge.map(function (feature) {
    const dateTimeStr = ee.String(feature.get('dateTime'));
    const fusoHorarioNumerico = ee.Number(timezone);
    const dateTime = ee.Date.parse('yyyy-MM-dd HH:mm:ss', dateTimeStr).advance(fusoHorarioNumerico, 'hour');
    return feature.set('dateTime', dateTime);
  });

  /**
   * @function addDatetimeProperty
   * @description Sets a `datetime` property on the image from its `system:time_start`.
   * @param {ee.Image} image - Image to tag.
   * **Called by:
   * @see {WaterlineIdentificationAndLevelAssigment} - via the image collection's map, before processing.
   * @memberof module:Intertidal
   * @returns {ee.Image} The image with the `datetime` property set.
   */
  var addDatetimeProperty = function (image) {
    var datetime = ee.Date(image.get('system:time_start'));
    return image.set('datetime', datetime);
  };

  /**
   * @function calculateTimeDifferenceAndSetProperties
   * @description Computes the absolute time difference (in seconds) between an image and a tide
   * gauge feature, tagging the feature with that difference and both dates.
   * @param {ee.Image} image - Image with a `datetime` property.
   * @param {ee.Feature} feature - Tide gauge feature with a `dateTime` property.
   * **Called by:
   * @see {getClosestFeatureImageAndDado}
   * @memberof module:Intertidal
   * @returns {ee.Feature} The feature tagged with `timeDifference`, `dateImage`, and `dateGauge`.
   */
  var calculateTimeDifferenceAndSetProperties = function (image, feature) {
    var imageDate = ee.Date(image.get('datetime'));
    var featureDate = ee.Date(feature.get('dateTime'));
    var diff = featureDate.difference(imageDate, 'second').abs();
    return feature.set({
      timeDifference: diff,
      dateImage: imageDate,
      dateGauge: featureDate,
    });
  };

  var histNDWI = [];
  /**
   * @function identifyWaterFeature
   * @description Identifies the intertidal water/land boundary from an NDWI image: computes its
   * Otsu threshold, adjusts it by the pixel standard deviation, then vectorizes the region below
   * that adjusted threshold. Not currently called anywhere in the codebase.
   * @param {ee.Image} image - Image with the given NDWI band.
   * @param {ee.Geometry} geometry - Area of interest geometry.
   * @param {Number} scale - Vectorization scale, in meters.
   * @param {String} band - Name of the band to compute the pixel standard deviation from.
   * @see {@link thresholdingAlgorithm}
   * @memberof module:Intertidal
   * @returns {ee.Geometry|null} The vectorized intertidal feature's geometry, or null if empty.
   */
  var identifyWaterFeature = function (image, geometry, scale, band) {
    var internalBandName = 'ndwi';
    var ndwi = image;
    var histogram = ndwi.reduceRegion({
      reducer: ee.Reducer.histogram(),
      geometry: geometry,
      scale: 10,
    });
    histNDWI = histogram.get('ndwi');
    var threshold = thresholdingAlgorithm(histogram.get('histogram'));

    var stdDevPixelValue = ndwi.reduceRegion({
      reducer: ee.Reducer.stdDev(),
      geometry: geometry,
      scale: 10,
      maxPixels: 1e9,
    });
    stdDevPixelValue = stdDevPixelValue.get(band).getInfo();
    var THajustment = ee.Number(stdDevPixelValue).add(1);
    var thresholdValue = ee.Number(threshold).multiply(ee.Number(THajustment));

    var intertidalFeature = ee.FeatureCollection(
      ndwi
        .clip(geometry)
        .lte(thresholdValue)
        .reduceToVectors({ scale: scale, maxPixels: 1e12, eightConnected: false })
        .filter(ee.Filter.eq('label', 1)),
    );
    var intertidalFeatureList = intertidalFeature.toList(intertidalFeature.size());
    if (intertidalFeature.getInfo() === null) {
      return null;
    } else {
      return intertidalFeature.geometry();
    }
  };

  /**
   * @function gaussianKernel
   * @description Builds a normalized discrete Gaussian kernel (weights summing to 1) of the given
   * size, mean, and standard deviation.
   * @param {ee.Number} size - Number of samples in the kernel.
   * @param {ee.Number} mean - Mean of the Gaussian curve.
   * @param {ee.Number} sigma - Standard deviation of the Gaussian curve.
   * **Called by:
   * @see {linearGaussianFilter}
   * @memberof module:Intertidal
   * @returns {ee.List} Normalized kernel weights.
   */
  var gaussianKernel = function (size, mean, sigma) {
    var gaussianCurve = function (x, mean, sigma) {
      var divider = ee.Number(sigma).multiply(ee.Number(2).multiply(Math.PI).sqrt());
      var exponent = ee.Number(-1).multiply(
        ee
          .Number(x)
          .subtract(mean)
          .pow(2)
          .divide(ee.Number(2).multiply(ee.Number(sigma).pow(2))),
      );
      return ee.Number(1).divide(divider).multiply(exponent.exp());
    };
    var half = ee.Number(size).divide(2).floor();
    var begin = ee.Number(0).subtract(half),
      end = ee.Number(0).add(half);
    var kernel = ee.List.sequence(begin, end).map(function (i) {
      return gaussianCurve(i, mean, sigma);
    });
    var sum = kernel.reduce(ee.Reducer.sum());
    var normalizedKernel = kernel.map(function (val) {
      return ee.Number(val).divide(sum);
    });
    return normalizedKernel;
  };

  /**
   * @function linearGaussianFilter
   * @description Smooths a line's coordinates with a 3-sample Gaussian filter: slides the kernel
   * along the coordinate list, weighting and summing each windowed longitude/latitude, while
   * keeping the original first and last points fixed. Returns the original coordinates unchanged
   * if the list is shorter than the kernel.
   * @param {ee.List} coordinates - Line coordinates as [lng, lat] pairs.
   * @see {@link gaussianKernel}
   * **Called by:
   * @see {calculateWaterlineForImage}
   * @memberof module:Intertidal
   * @returns {ee.List} Smoothed coordinates (or the original list, if too short to smooth).
   */
  var linearGaussianFilter = function (coordinates) {
    var samples = 3;
    var mean = 0;
    var sd = 1;
    var coordinateList = ee.List(coordinates);
    var kernelSize = ee.Number(samples);
    var kernelMean = ee.Number(mean);
    var kernelSd = ee.Number(sd);
    var kernel = gaussianKernel(kernelSize, kernelMean, kernelSd);
    var first = coordinateList.reduce(ee.Reducer.first()),
      last = coordinateList.reduce(ee.Reducer.last());
    var sequence = ee.List.sequence(ee.Number(0), coordinateList.length().subtract(kernelSize));
    var path = sequence.map(function (index) {
      var interval = coordinateList.slice(ee.Number(index), ee.Number(index).add(kernelSize)).zip(kernel);
      var gaussian = interval.map(function (element) {
        var asList = ee.List(element);
        var point = ee.List(asList.get(0));
        var weight = ee.Number(asList.get(1));
        return point.map(function (value) {
          return ee.Number(value).multiply(weight);
        });
      });
      var smoothenLong = gaussian
        .map(function (point) {
          return ee.List(point).get(0);
        })
        .reduce(ee.Reducer.sum());
      var smoothenLat = gaussian
        .map(function (point) {
          return ee.List(point).get(1);
        })
        .reduce(ee.Reducer.sum());
      return ee.List([smoothenLong, smoothenLat]);
    });
    var smoothen = ee.List([]).add(first).cat(path).add(last);
    return ee.Algorithms.If(coordinateList.size().lte(kernelSize), coordinateList, smoothen);
  };

  /**
   * @function getClosestFeatureImageAndDado
   * @description Finds the tide gauge feature closest in time to the given waterline image and
   * extracts its tide value and both dates.
   * @param {ee.Image} waterlineImage - Image with a `datetime` property.
   * @param {ee.FeatureCollection} tideGauge - Tide gauge readings to search.
   * @see {@link calculateTimeDifferenceAndSetProperties}
   * **Called by:
   * @see {calculateWaterlineForImage} - via processAllValidFeatures.
   * @memberof module:Intertidal
   * @returns {Object} {closestFeatureImage, tideValue, dateImage, dateGauge}
   */
  function getClosestFeatureImageAndDado(waterlineImage, tideGauge) {
    var closestCollection = tideGauge.map(function (feature) {
      return calculateTimeDifferenceAndSetProperties(waterlineImage, feature);
    });
    var sortedCollection = closestCollection.sort('timeDifference');
    var closestFeatureImage = sortedCollection.first();
    var tideValue = closestFeatureImage.get('Dado');
    var dateImage = closestFeatureImage.get('dateImage');
    var dateGauge = closestFeatureImage.get('dateGauge');
    return {
      closestFeatureImage: closestFeatureImage,
      tideValue: tideValue,
      dateImage: dateImage,
      dateGauge: dateGauge,
    };
  }

  /**
   * @function waterLandFilter
   * @description Masks out all NDWI pixels at or above 0.2 (water surface), keeping only
   * flooding/humidity and drier land ranges. Not currently called anywhere in the codebase.
   * @param {ee.Image} image - NDWI image.
   * @memberof module:Intertidal
   * @returns {ee.Image} The image with water pixels masked out.
   */
  var waterLandFilter = function (image) {
    return image.updateMask(image.lt(0.2));
  };

  /**
   * @function filterPolygons
   * @description Drops polygons with 5 or fewer vertices. Not currently called anywhere in the
   * codebase.
   * @param {ee.List} polygon - A polygon's coordinate rings.
   * @memberof module:Intertidal
   * @returns {ee.List|null} The polygon, or null if it has too few vertices.
   */
  var filterPolygons = function (polygon) {
    var numVertices = ee.List(ee.List(polygon).get(0)).length();
    return ee.Algorithms.If(numVertices.gt(5), polygon, null);
  };

  /**
   * @function getPolygonWithMostVertices
   * @description Returns the feature with the most vertices from a collection. Not currently
   * called anywhere in the codebase.
   * @param {ee.FeatureCollection} featureCollection - Features tagged with a `numVertices` property.
   * @memberof module:Intertidal
   * @returns {ee.Feature} The feature with the highest `numVertices`, or a null feature if empty.
   */
  var getPolygonWithMostVertices = function (featureCollection) {
    var size = featureCollection.size();
    var result = ee.Algorithms.If(size.gt(0), featureCollection.sort('numVertices', false).first(), null);
    return ee.Feature(result);
  };

  let selectedPolyline = null;
  let selectedInfoWindow = null;
  /**
   * @function plotEvaluatedSync
   * @description Draws each LineString feature as an interactive Google Maps polyline: clicking a
   * line highlights it (deselecting any previously selected line) and opens an InfoWindow listing
   * its properties.
   * @param {google.maps.Map} map - Map to draw on.
   * @param {Object} evaluatedData - GeoJSON FeatureCollection of LineString features.
   * @param {String} colorForLines - Stroke color applied to every line.
   * **Called by:
   * @see {WaterlineIdentificationAndLevelAssigment} - via startWaterlineRun, printWaterlines, and fetchAndPlotSingleWaterline.
   * @memberof module:Intertidal
   * @returns {void}
   */
  function plotEvaluatedSync(map, evaluatedData, colorForLines) {
    if (!map) {
      console.error('Mapa não disponível.');
      return;
    }
    if (!evaluatedData || !evaluatedData.features) {
      console.error('Dados avaliados inválidos.');
      return;
    }

    evaluatedData.features.forEach((feature) => {
      const geometry = feature.geometry;
      if (geometry.type === 'LineString') {
        const path = geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));

        const color = colorForLines;

        const polyline = new window.google.maps.Polyline({
          path,
          map: map,
          strokeColor: color,
          strokeOpacity: 1.0,
          strokeWeight: 2,
        });

        polyline.addListener('click', (e) => {
          if (selectedPolyline) {
            selectedPolyline.setOptions({
              strokeWeight: 2,
              strokeColor: selectedPolyline.originalColor,
            });
          }
          if (selectedInfoWindow) {
            selectedInfoWindow.close();
          }

          polyline.setOptions({
            strokeWeight: 5,
            strokeColor: '#FF00FF',
          });

          polyline.originalColor = color;
          selectedPolyline = polyline;

          const content =
            Object.entries(feature.properties || {})
              .map(([key, val]) => {
                if (key === 'tideValue' && typeof val === 'number') {
                  val = val.toFixed(2);
                }
                const label = t(`intertidal.attributes.${key}`, key);
                return `<strong>${label}:</strong> ${val}`;
              })
              .join('<br>') || t('forms.intertidal.attributes.noProperties');
          selectedInfoWindow = new window.google.maps.InfoWindow({
            content,
            position: e.latLng,
          });
          selectedInfoWindow.open(map);
        });
      }
    });
    console.log('Waterlines plotadas com interatividade.');
  }

  /**
   * @function printWaterlines
   * @description Evaluates the accumulated `evaluated` FeatureCollection, sanitizes it into
   * LineStrings, and plots it on the map. Not currently called anywhere in the codebase.
   * @see {@link convertGeoJSONToLineStrings}
   * @see {@link prepareGeoJsonForGoogleMaps}
   * @see {@link plotEvaluatedSync}
   * @memberof module:Intertidal
   * @returns {Promise<void>}
   */
  const printWaterlines = async () => {
    console.log('Printing waterlines....');
    try {
      console.log('executing getInfo for evaluated variable...');
      evaluatedEE = evaluated;
      evaluatedEE = await evaluatedEE.getInfo();
      console.log('Antes da sanitização', evaluatedEE);

      console.log('CONTEÚDO BRUTO (para geojson.io):', JSON.stringify(evaluatedEE, null, 2));

      const sanitizedEvaluated = convertGeoJSONToLineStrings(evaluatedEE);
      console.log('Depois da sanitização', sanitizedEvaluated);
      const geoJsonReady = prepareGeoJsonForGoogleMaps(sanitizedEvaluated);

      console.log('DEBUG FINAL - GeoJSON enviado para o mapa:', JSON.stringify(geoJsonReady, null, 2));

      console.log('Depois de geoJsonReady', geoJsonReady);
      if (geoJsonReady) {
        plotEvaluatedSync(map, sanitizedEvaluated);
      } else {
        console.error('Não foi possível preparar o GeoJSON.');
      }
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * @function fetchAndPlotSingleWaterline
   * @description Evaluates a single waterline FeatureCollection, sanitizes it, plots it on the
   * map, and accumulates it into `evaluatedEE` for later export. Not currently called anywhere
   * in the codebase.
   * @param {ee.FeatureCollection} waterlineEE - Waterline features for a single image.
   * @see {@link convertGeoJSONToLineStrings}
   * @see {@link prepareGeoJsonForGoogleMaps}
   * @see {@link plotEvaluatedSync}
   * @memberof module:Intertidal
   * @returns {Promise<void>}
   */
  const fetchAndPlotSingleWaterline = async (waterlineEE) => {
    try {
      const waterlineGeoJSON = await waterlineEE.getInfo();

      if (!waterlineGeoJSON || waterlineGeoJSON.features.length === 0) {
        console.log("Nenhuma linha d'água válida retornada para esta imagem.");
        return;
      }

      const sanitized = convertGeoJSONToLineStrings(waterlineGeoJSON);
      const geoJsonReady = prepareGeoJsonForGoogleMaps(sanitized);

      if (geoJsonReady) {
        plotEvaluatedSync(map, sanitized);

        if (!evaluatedEE) {
          evaluatedEE = { type: 'FeatureCollection', features: [] };
        }
        evaluatedEE.features.push(...sanitized.features);
      }
    } catch (e) {
      console.error("Erro ao buscar e plotar linha d'água individual:", e);
    }
  };

  return (
    <Box className={classes.wrapper}>
      <Grow in={!isDrawing} unmountOnExit>
        <Grid container justifyContent="center" spacing={0} id="mapScreen" className={classes.mapOverlay}>
          <Grid item xs={9} className={classes.mapContainer}>
            <ImageChooserCard vertical="intertidal" />
            <div>
              <ActionsCard />
              {isProcessing && (
                <Box
                  display="flex"
                  alignItems="center"
                  p={1}
                  bgcolor="background.paper"
                  borderRadius={4}
                  m={1}
                  boxShadow={3}>
                  <CircularProgress size={24} style={{ marginRight: '10px' }} />
                  <Typography variant="body2">
                    {t('forms.intertidal.waterlineProgress', { completed: currentImage, total: totalImages })}
                    {timeEstimate && ` [ Estimativa: ${timeEstimate} ]`}
                  </Typography>
                </Box>
              )}
            </div>
            <div>
              &nbsp;&nbsp;&nbsp;
              <Button
                color="primary"
                variant="contained"
                className={classes.button}
                onClick={returnToBegin}
                style={{ cursor: 'pointer' }}>
                {' '}
                {t('forms.acquisition.5.bathymetry.returnToBegin')}
              </Button>
              <br />
              <br />
              <br />
              <br />
            </div>
          </Grid>
          <Grid item xs={3}>
            <LoadedImagesAccordion />
          </Grid>
        </Grid>
      </Grow>
      <GoogleMap
        style={{ width: '100%', height: 500 }}
        onLoad={(createdMap) => {
          console.log('Mapa carregado via onLoad');
          setMap(createdMap);
          setMapReady(true);
        }}
      />

      <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
    </Box>
  );
};

export default WaterlineIdentificationAndLevelAssigment;
